import { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '@/components/ui/Card';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/supabase/client';
import VendorCardBlock from './VendorCardBlock';
import StartSession from './StartSession';
import { vendorCopy } from './_shop-helpers';
import { useTeacherBalance } from '@/hooks/useTeacherBalance';

interface VendorCard {
  id: string
  vendor: string
  verified_at?: string
}

interface SessionMetadata {
  vendor?: string
  partner?: string
  amount_cents?: number
  card_last4?: string
  alias_email?: string
}

interface ShoppingSession {
  id: string
  status: string
  metadata?: SessionMetadata
  started_at?: string
  updated_at?: string
}

const VENDORS = Object.keys(vendorCopy);

function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatStatus(status: string = ''): string {
  if (!status) return '—';
  return status
    .toString()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function TeacherShopping() {
  const { session, profile } = useAuth();
  const token = session?.access_token || '';
  const {
    loading: balanceLoading,
    error: balanceError,
    totalCents: availableCents,
    refresh: refreshBalance,
  } = useTeacherBalance(token || null, { role: profile?.role || null });

  const [cards, setCards] = useState<VendorCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');

  const [sessions, setSessions] = useState<ShoppingSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');

  const fetchCards = useCallback(async () => {
    if (!token) {
      setCards([]);
      return;
    }

    setCardsLoading(true);
    setCardsError('');

    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/cards/vendor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message;
        throw new Error(errMsg || 'Unable to load cards');
      }
      const payload = raw?.data ?? raw;
      setCards(Array.isArray(payload.items) ? payload.items : []);
    } catch (error) {
      setCards([]);
      setCardsError((error as Error)?.message || 'Unable to load cards');
    } finally {
      setCardsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const cardsByVendor = useMemo(() => {
    return (cards || []).reduce<Record<string, VendorCard>>((acc, card) => {
      if (!card?.vendor) return acc;
      acc[card.vendor] = card;
      return acc;
    }, {});
  }, [cards]);

  const fetchSessions = useCallback(async () => {
    if (!session) {
      setSessions([]);
      return;
    }

    setSessionsLoading(true);
    setSessionsError('');

    const { data, error } = await supabase
      .from('shopping_sessions')
      .select('id, status, metadata, started_at, updated_at')
      .order('started_at', { ascending: false })
      .limit(10);

    if (error) {
      setSessionsError(error.message || 'Unable to load sessions');
      setSessions([]);
    } else {
      setSessions((data || []) as ShoppingSession[]);
    }

    setSessionsLoading(false);
  }, [session]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleCardSaved = useCallback(() => {
    fetchCards();
  }, [fetchCards]);

  const handleSessionStarted = useCallback(() => {
    fetchSessions();
    refreshBalance();
  }, [fetchSessions, refreshBalance]);

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-sand bg-white/70 p-4 shadow-soft">
        <p className="text-sm font-medium text-cocoa">Available balance</p>
        <div className="mt-2 text-2xl font-semibold text-cocoa">{formatCurrency(availableCents)}</div>
        {balanceLoading ? <p className="mt-1 text-xs text-cocoa/70">Checking balances…</p> : null}
        {balanceError ? <p className="mt-1 text-xs text-brick">{balanceError}</p> : null}
      </div>

      <div className="space-y-2">
        {cardsError ? <p className="text-sm text-brick">{cardsError}</p> : null}
        {cardsLoading ? <p className="text-sm text-cocoa/70">Checking saved cards…</p> : null}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {VENDORS.map((vendor) => {
          const card = cardsByVendor[vendor];
          return (
            <div key={vendor} className="space-y-4">
              <VendorCardBlock
                vendor={vendor}
                initial={card}
                token={token}
                onSaved={handleCardSaved}
              />
              <StartSession
                vendor={vendor}
                token={token}
                isVerified={Boolean(card?.verified_at)}
                availableCents={availableCents ?? null}
                balanceLoading={balanceLoading}
                balanceError={balanceError}
                onStarted={handleSessionStarted}
              />
            </div>
          );
        })}
      </div>

      <Card className="space-y-4">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-cocoa">Recent shopping sessions</h3>
            <p className="text-sm text-cocoa/70">Track sessions linked to your verified vendor cards.</p>
          </div>
          <button
            type="button"
            onClick={fetchSessions}
            disabled={sessionsLoading}
            className="btn bg-white text-cocoa shadow-none ring-1 ring-sand hover:bg-shell disabled:cursor-not-allowed disabled:opacity-70"
          >
            {sessionsLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </header>

        {sessionsError ? <p className="text-sm text-brick">{sessionsError}</p> : null}

        {!sessionsLoading && sessions.length === 0 ? (
          <p className="text-sm text-cocoa/70">No sessions yet. Start one above to see it here.</p>
        ) : null}

        <div className="space-y-3">
          {sessions.map((sessionItem) => {
            const meta = sessionItem?.metadata || {};
            const vendorKey = meta.vendor || meta.partner || '';
            const vendorLabel = (vendorCopy as Record<string, { label?: string }>)[vendorKey]?.label || formatStatus(vendorKey || 'Vendor');
            const amount = Number(meta.amount_cents);
            const started = sessionItem.started_at || sessionItem.updated_at;
            const cardLast4 = meta.card_last4 ? `•••• ${meta.card_last4}` : '—';

            return (
              <div
                key={sessionItem.id}
                className="space-y-3 rounded-2xl border border-sand bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-cocoa">{vendorLabel}</p>
                    <p className="text-xs text-cocoa/70">Started {formatDate(started)}</p>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-shell px-3 py-1 text-xs font-semibold text-cocoa">
                    {formatStatus(sessionItem.status)}
                  </span>
                </div>

                <div className="grid gap-2 text-sm text-cocoa/80 sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Amount</p>
                    <p>{formatCurrency(amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Card</p>
                    <p>{cardLast4}</p>
                  </div>
                  {meta.alias_email ? (
                    <div className="sm:col-span-2">
                      <p className="text-xs uppercase tracking-wide text-cocoa/60">Purchase email</p>
                      <p>{meta.alias_email}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
